import React from 'react';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const HouseWashing = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const challenges = [
    { bold: "Cedar siding turning black", text: "with mildew and algae." },
    { bold: "Salt air and sandy winds", text: "dulling stucco, vinyl, and painted wood." },
    { bold: "Roofs streaked with algae and moss", text: "that shorten shingle life." },
    { bold: "Decks, patios, and stonework", text: "stained with mildew and buildup." }
  ];

  const services = [
    "House Washing",
    "Roof Cleaning",
    "Deck & Patio Cleaning",
    "Power Washing",
    "Soft Washing",
    "Pressure Washing",
    "Surface Cleaning",
    "Exterior Building Cleaning"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <ServiceHero
        title="House & Roof Washing"
        headline="Dirty Home?"
        description="Safe residential and commercial house and roof washing with our soft-wash system — removes moss, streaks, and algae without damaging surfaces."
        backgroundImage="/images/house-washing/house-wash-hero.jpg"
        backgroundVideo="/powerwash-video.mp4"
        defaultService="House Washing"
      />
      
      {/* Content Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-6">
                Our House & Roof Washing Services
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>, we specialize in <strong>soft washing</strong> — the safest, most effective 
                method for <strong>house washing and roof cleaning</strong> in the Hamptons. Unlike harsh 
                pressure washing, our low-pressure system (under 500 PSI) gently removes 
                <strong> mold, mildew, algae, moss, salt, and dirt</strong> without damaging delicate surfaces 
                like <strong>cedar shake siding, stucco, painted wood, vinyl, or shingles</strong>.
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                That's why soft <strong>washing is the most recommended</strong> method for siding, roofing, 
                and other exterior surfaces. It doesn't just wash the surface — it kills algae, mold, 
                and mildew at the root, keeping your home <strong>cleaner for longer</strong>.
              </p>

              <p className="text-gray-700 font-medium mb-4">
                Hamptons homes face unique challenges:
              </p>

              <ul className="space-y-3 mb-8">
                {challenges.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      <strong>{item.bold}</strong> {item.text}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="text-gray-700 font-medium mb-4">
                Our suite of residential and commercial soft washing services includes:
              </p>

              <ul className="space-y-3 mb-8">
                {services.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="mb-8">
                <p className="font-semibold text-gray-900 mb-2">We Also Offer:</p>
                <p className="text-gray-700">
                  Beyond House Washing And Roof Cleaning, our team is trusted across the 
                  Hamptons for <strong>Window Cleaning, Solar Panel Cleaning, Gutter Cleaning, and 
                  Shrink Wrapping</strong>.{' '}
                  <Link to="/#services" className="text-blue-600 hover:text-blue-700 font-medium">
                    Explore all our services <ArrowRight className="inline w-4 h-4" />
                  </Link>
                </p>
              </div>

              <button
                onClick={scrollToQuote}
                data-testid="house-washing-cta"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Request a Quote
              </button>
              <p className="mt-3 text-sm text-gray-600">
                Get within 1 business day
              </p>
            </div>

            {/* Right Images */}
            <div className="relative">
              <div className="grid grid-cols-1 gap-6">
                {/* Main Image */}
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_clean-hamptons-demo/artifacts/90x60b57_Powerwashing%20Photo.png"
                    alt="Professional house washing service"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Top Right */}
                  <div className="absolute -top-4 -right-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8" />
                      <div>
                        <div className="text-2xl font-bold">15+ Years</div>
                        <div className="text-sm text-blue-100">Of Experience</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Image */}
                <div className="relative">
                  <img
                    src="/images/before-after/house-wash-2.jpg"
                    alt="Cedar siding restoration before and after"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  {/* Stats Badge - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-xl">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">1450+ Clients</div>
                        <div className="text-sm text-gray-600">Satisfied By Us</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Restore Your Home's Beauty?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get a free, no-obligation quote within 1 business day. 
            Our soft washing experts are ready to make your home look brand new.
          </p>
          <Link
            to="/#quote"
            data-testid="house-washing-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default HouseWashing;
